import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, Text2 } from 'components'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { NotificationDialog } from 'dialogs/NotificationDialog'
import { YesNoDialog } from 'dialogs/YesNoDialog'
import { FormikActions } from 'formik'
import {
  ProvingDetailsOrderIdForm,
  ProvingDetailsTimespanForm,
} from 'forms/ProvingDetailsForm'
import { MaterialDetailsForm } from 'forms/ProvingDetailsForm/materials/MaterialDetailsForm'
import { ProvingDetailsMaterialsAnnotationForm } from 'forms/ProvingDetailsForm/materials/ProvingDetailsMaterialsAnnotationForm'
import {
  ProvingDetailsAddMaterialForm,
  ProvingDetailsAddMaterialValues,
  ProvingDetailsMaterialsFormSchema,
} from 'forms/ProvingDetailsForm/materials/ProvingDetailsMaterialsForm'
import { ProvingDetailsSimpleTransportForm } from 'forms/ProvingDetailsForm/materials/ProvingDetailsSimpleTransportForm'
import { ProvingDetailsSimpleTransportStatusForm } from 'forms/ProvingDetailsForm/materials/ProvingDetailsSimpleTransportStatusForm'
import { ProvingDetailsAmountForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAmountForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import { ProvingDetailsGroupForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsGroupForm'
import {
  ProvingTransportDetailsForm,
  ProvingTransportDetailsFormSchema,
} from 'forms/ProvingDetailsForm/shared/ProvingTransportDetailsForm'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import {
  useCommand,
  useDeselectCommand,
  useGroupCommand,
} from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import {
  BookingStatus,
  ProvingMaterial,
  ProvingMaterialBatchInput,
  ProvingMaterialInput,
  ProvingMaterialUpdateInput,
  ProvingType,
  ProvingUserRole,
  TransportType,
  UserRole,
} from 'model'
import { IconButton, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Page, Section } from './Layout'
import {
  useAddProvingMaterialsMutation,
  useDeleteProvingMaterialMutation,
  useExportMaterialsToSapMutation,
  useUpdateProvingMaterialMutation,
} from './hooks/PageMaterials.generated'
import { useUpdateProvingMutation } from './hooks/ProvingDetailsView.generated'
import { MaterialList } from './lists/MaterialList'

// FormType used to show what form was clicked.
enum FormType {
  addMaterial,
  orderId,
  transport,
  transportStatus,
  amount,
  annotation,
  annotationMaterials,
  timespan,
}

export const PageMaterials: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const materials = proving.materials || []
  const isProvingAdmin = isInProvingRole(proving, ProvingUserRole.Administrator)
  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )

  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const canUpdateStatus = () => {
    if (
      isProvingAdmin ||
      isAdmin ||
      isProvingResourceManager ||
      isProvingEditor ||
      isServiceTeam
    ) {
      return true
    } else {
      return false
    }
  }

  const [, setSelectedItemId] = React.useState()
  const [updateProving] = useUpdateProvingMutation({ onCompleted: refetch })
  const [addProvingMaterials] = useAddProvingMaterialsMutation()
  const [updateProvingMaterials] = useUpdateProvingMaterialMutation()
  const [deleteProvingMaterials] = useDeleteProvingMaterialMutation()
  const [exportMaterialsToSap] = useExportMaterialsToSapMutation()
  const [sapExportLoading, setSapExportLoading] = React.useState(false)
  const [selection, selectedItems] = useSelection<ProvingMaterial>({
    getKey: (item) => item._id,
  })
  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()

  const [formState, setFormState] = React.useState<FormType>()
  const [panelState, showPanel, hidePanel] = useFormikPanelState({
    initialValues: {},
  })

  const [timespanPanelState, showTimespanPanel] = useFormikPanelState()

  const [deleteDialogState, showDeleteDialog, hideDeleteDialog] =
    useDialogState()

  const [multiEditPanelState, showMultiEditPanel, hideMultiEditPanel] =
    useFormikPanelState()

  const [addGroupPanelState, showAddGroupPanel, hideAddGroupPanel] =
    useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const [transportStatusPanelState, hideTransportStatusPanel] =
    useFormikPanelState()
  const [
    transportDataPanelState,
    showTransportDataPanel,
    hideTransportDataPanel,
  ] = useFormikPanelState()
  const [confirmTpoDialogState, showConfirmTpoDialog, hideConfirmTpoDialog] =
    useDialogState()
  const [tpoSuccessDialogState, showTpoSuccessDialog, hideTpoSuccessDialog] =
    useDialogState()

  const [returnTransport, setReturnTransport] = React.useState(false)
  const [outboundTransport, setOutboundTransport] = React.useState(false)

  let canUpdateCurrentProving = canUpdateProving(proving)
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }

  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead

  const handleConfirmDelete = React.useCallback(async () => {
    await deleteProvingMaterials({
      variables: {
        provingId: proving.id,
        idsToDelete: selectedItems.map((sel) => sel._id),
      },
    })
    selection.setAllSelected(false)
    refetch()
    hideDeleteDialog()
  }, [
    hideDeleteDialog,
    selection,
    selectedItems,
    proving,
    deleteProvingMaterials,
    refetch,
  ])
  type ProvingMaterialInputWithKey = ProvingMaterialInput & { key?: string }
  const handleAddMaterialSubmit = React.useCallback(
    async (
      values: { materials: ProvingMaterialInputWithKey[] },
      actions: FormikActions<ProvingDetailsAddMaterialValues>
    ) => {
      actions.setSubmitting(true)
      const newValues = values.materials
      newValues.forEach((x) => {
        if (x.hasOwnProperty('key')) {
          delete x.key
        }

        if (x.transport) {
          x.transport.bookingStatus = BookingStatus.NotRequired
          x.transport.transportType = TransportType.NoTransport
        }

        return x
      })

      const { errors } = await addProvingMaterials({
        variables: {
          provingId: proving.id,
          inputs: newValues,
        },
      })

      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hidePanel()
    },
    [hidePanel, proving, addProvingMaterials, refetch, selection]
  )

  const handleSubmitMulti = React.useCallback(
    async (
      values: ProvingMaterialUpdateInput,
      actions: FormikActions<ProvingMaterialUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batches: ProvingMaterialBatchInput[] = []

      selectedItems.forEach(async (rr) => {
        batches.push({ _id: rr._id, update: values })
      })

      const { errors } = await updateProvingMaterials({
        variables: { provingId: proving.id, batch: batches },
      })
      if (errors) {
        throw errors
      }
      selection.setAllSelected(false)
      actions.setSubmitting(false)
      refetch()
      hideMultiEditPanel()
      hideTransportStatusPanel()
    },
    [
      proving,
      updateProvingMaterials,
      hideMultiEditPanel,
      hideTransportStatusPanel,
      selectedItems,
      refetch,
      selection,
    ]
  )
  const handleGroupAssign = React.useCallback(
    async (
      values: { group: string },
      actions: FormikActions<{ group: string }>
    ) => {
      actions.setSubmitting(true)

      const batch: ProvingMaterialBatchInput[] = selectedItems.map((m) => ({
        _id: m._id,
        update: {
          group: values.group,
        },
      }))

      await updateProvingMaterials({
        variables: {
          provingId: proving.id,
          batch,
        },
      })

      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideAddGroupPanel()
    },
    [
      selection,
      selectedItems,
      refetch,
      hideAddGroupPanel,
      proving,
      updateProvingMaterials,
    ]
  )
  const handleRemoveGroup = React.useCallback(async () => {
    const batch: ProvingMaterialBatchInput[] = selectedItems.map((m) => ({
      _id: m._id,
      update: {
        group: '',
      },
    }))

    await updateProvingMaterials({
      variables: {
        provingId: proving.id,
        batch,
      },
    })
    selection.setAllSelected(false)
    refetch()
  }, [proving, selectedItems, updateProvingMaterials, refetch, selection])

  useDeselectCommand(
    selectedItems.length,
    {
      priority: 0,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )
  useCommand(
    {
      key: 'delete',
      priority: 10,
      farCommand: true,
      text: 'Material entfernen',
      hidden: selectedItems.length === 0,
      disabled: !canUseCommand,
      iconProps: { iconName: 'Delete' },
      onClick: () => showDeleteDialog(),
    },
    [selectedItems, canUpdateCurrentProving]
  )
  // #region Form panel hooks

  useCommand(
    {
      key: 'addMaterial',
      priority: 10,
      text: t('model.proving.materials.addMaterial'),
      iconProps: { iconName: 'ShoppingCartSolid' },
      disabled: !canUseCommand,
      onClick: () => {
        setFormState(FormType.addMaterial)
        showPanel({
          materials: [],
        })
      },
    },
    [setFormState, canUpdateCurrentProving]
  )

  //#region TIMESPAN
  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: proving.dateFrom,
        dateTo: proving.dateTo,
      },
    }))

    await updateProvingMaterials({
      variables: {
        provingId: proving.id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateProvingMaterials, refetch])

  useCommand(
    {
      key: 'timespan',
      priority: 20,
      disabled: selectedItems.length === 0 || !canUseCommand,
      text: t('field.timespan.label'),
      iconProps: { iconName: 'CalendarWeek' },
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              selectedItems.length > 1
                ? showTimespanPanel({})
                : showTimespanPanel({
                    dateTo: selectedItems[0].dateTo,
                    dateFrom: selectedItems[0].dateFrom,
                  })
              setFormState(FormType.timespan)
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  useCommand(
    {
      key: 'editAmount',
      priority: 20,
      text: t('model.proving.resourceRequirements.editAmount'),
      disabled: selectedItems.length === 0 || !canUseCommand,
      iconProps: { iconName: 'NumberField' },
      onClick: () => {
        selectedItems.length > 1
          ? showMultiEditPanel({})
          : showMultiEditPanel({ amount: selectedItems[0].amount })
        setFormState(FormType.amount)
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )

  //#region ORDERID
  const setNoOrderId = React.useCallback(async () => {
    const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        orderId: '',
      },
    }))

    await updateProvingMaterials({
      variables: {
        provingId: proving.id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateProvingMaterials, refetch])
  useCommand(
    {
      key: 'orderId',
      priority: 20,
      text: t('model.proving.participant.orderId'),
      disabled: selectedItems.length === 0 || !canUseCommand,
      iconProps: { iconName: 'DonutChart' },
      subMenuProps: {
        items: [
          {
            key: 'assignOrderId',
            text: t('field.orderId.assignOrderId'),
            onClick: () => {
              selectedItems.length > 1
                ? showMultiEditPanel({})
                : showMultiEditPanel({ orderId: selectedItems[0].orderId })
              setFormState(FormType.orderId)
            },
          },
          {
            key: 'noOrderId',
            text: t('field.orderId.noOrderId'),
            onClick: () => {
              setNoOrderId()
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion ORDERID

  //#region TRANSPORT
  const handleTransportBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateProvingMaterials({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateProvingMaterials, refetch])

  const handleTransportBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        transport: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateProvingMaterials({
      variables: {
        batch,
        provingId: proving.id,
      },
    })

    refetch()
  }, [proving, selectedItems, updateProvingMaterials, refetch])

  const handleTransportBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateProvingMaterials({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateProvingMaterials, refetch])

  const handleTransportBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateProvingMaterials({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateProvingMaterials, refetch])

  // const handleOutboundAndReturnTransport = React.useCallback(async () => {
  //   const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) =>
  //     sel.transport && sel.transport.bookingStatus !== BookingStatus.NotRequired
  //       ? {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               transportType: TransportType.OutboundAndReturnTransport,
  //             },
  //           },
  //         }
  //       : {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               bookingStatus: BookingStatus.Open,
  //               transportType: TransportType.OutboundAndReturnTransport,
  //             },
  //           },
  //         }
  //   )

  //   await updateProvingMaterials({
  //     variables: {
  //       batch,
  //       provingId: proving.id,
  //     },
  //   })

  //   refetch()
  // }, [proving, selectedItems, updateProvingMaterials, refetch])

  // const handleOutboundTransport = React.useCallback(async () => {
  //   const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) =>
  //     sel.transport && sel.transport.bookingStatus !== BookingStatus.NotRequired
  //       ? {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               transportType: TransportType.OutboundTransport,
  //             },
  //           },
  //         }
  //       : {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               bookingStatus: BookingStatus.Open,
  //               transportType: TransportType.OutboundTransport,
  //             },
  //           },
  //         }
  //   )

  //   await updateProvingMaterials({
  //     variables: {
  //       batch,
  //       provingId: proving.id,
  //     },
  //   })

  //   refetch()
  // }, [proving, selectedItems, updateProvingMaterials, refetch])

  // const handleReturnTransport = React.useCallback(async () => {
  //   const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) =>
  //     sel.transport && sel.transport.bookingStatus !== BookingStatus.NotRequired
  //       ? {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               transportType: TransportType.ReturnTransport,
  //             },
  //           },
  //         }
  //       : {
  //           _id: sel._id,
  //           update: {
  //             transport: {
  //               bookingStatus: BookingStatus.Open,
  //               transportType: TransportType.ReturnTransport,
  //             },
  //           },
  //         }
  //   )

  //   await updateProvingMaterials({
  //     variables: {
  //       batch,
  //       provingId: proving.id,
  //     },
  //   })

  //   refetch()
  // }, [proving, selectedItems, updateProvingMaterials, refetch])

  const handleNoTransport = React.useCallback(async () => {
    const batch: ProvingMaterialBatchInput[] = selectedItems.map((sel) =>
      sel.transport && sel.transport.bookingStatus !== BookingStatus.NotRequired
        ? {
            _id: sel._id,
            update: {
              transport: {
                transportType: TransportType.NoTransport,
              },
            },
          }
        : {
            _id: sel._id,
            update: {
              transport: {
                bookingStatus: BookingStatus.Open,
                transportType: TransportType.NoTransport,
              },
            },
          }
    )

    await updateProvingMaterials({
      variables: {
        batch,
        provingId: proving.id,
      },
    })

    refetch()
  }, [proving, selectedItems, updateProvingMaterials, refetch])

  const getDefaultTransportData = (
    transportType: TransportType,
    selectedItem: ProvingMaterial
  ) => {
    const provingGround = proving.provingGround
    let res = {}
    if (provingGround) {
      switch (transportType) {
        case TransportType.OutboundAndReturnTransport:
          res = {
            outboundTransportSender:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportSender
                ? selectedItem.transport.outboundTransportSender
                : provingGround.outboundTransportSender,
            outboundTransportSenderTime:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportSenderTime,
            outboundTransportSenderDate:
              (selectedItem.transport &&
                selectedItem.transport.outboundTransportSenderDate) ||
              undefined,
            outboundTransportRecipient:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportRecipient
                ? selectedItem.transport.outboundTransportRecipient
                : provingGround.outboundTransportRecipient,
            outboundTransportRecipientTime:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportRecipientTime,
            outboundTransportRecipientDate: selectedItem.dateFrom,
            returnTransportSender:
              selectedItem.transport &&
              selectedItem.transport.returnTransportSender
                ? selectedItem.transport.returnTransportSender
                : provingGround.returnTransportSender,
            returnTransportSenderTime:
              selectedItem.transport &&
              selectedItem.transport.returnTransportSenderTime,
            returnTransportSenderDate: selectedItem.dateTo,
            returnTransportRecipient:
              selectedItem.transport &&
              selectedItem.transport.returnTransportRecipient
                ? selectedItem.transport.returnTransportRecipient
                : provingGround.returnTransportRecipient,
            returnTransportRecipientTime:
              selectedItem.transport &&
              selectedItem.transport.returnTransportRecipientTime,
            returnTransportRecipientDate:
              (selectedItem.transport &&
                selectedItem.transport.returnTransportRecipientDate) ||
              undefined,
          }
          break
        case TransportType.OutboundTransport:
          res = {
            outboundTransportSender:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportSender
                ? selectedItem.transport.outboundTransportSender
                : provingGround.outboundTransportSender,
            outboundTransportSenderTime:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportSenderTime,
            outboundTransportSenderDate:
              (selectedItem.transport &&
                selectedItem.transport.outboundTransportSenderDate) ||
              undefined,
            outboundTransportRecipient:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportRecipient
                ? selectedItem.transport.outboundTransportRecipient
                : provingGround.outboundTransportRecipient,
            outboundTransportRecipientTime:
              selectedItem.transport &&
              selectedItem.transport.outboundTransportRecipientTime,
            outboundTransportRecipientDate: selectedItem.dateFrom,
            returnTransportSender: '',
            returnTransportSenderTime: '',
            returnTransportSenderDate: null,
            returnTransportRecipient: '',
            returnTransportRecipientTime: '',
            returnTransportRecipientDate: null,
          }
          break
        case TransportType.ReturnTransport:
          res = {
            outboundTransportSender: '',
            outboundTransportSenderTime: '',
            outboundTransportSenderDate: null,
            outboundTransportRecipient: '',
            outboundTransportRecipientTime: '',
            outboundTransportRecipientDate: null,
            returnTransportSender:
              selectedItem.transport &&
              selectedItem.transport.returnTransportSender
                ? selectedItem.transport.returnTransportSender
                : provingGround.returnTransportSender,
            returnTransportSenderTime:
              selectedItem.transport &&
              selectedItem.transport.returnTransportSenderTime,
            returnTransportSenderDate: selectedItem.dateTo,
            returnTransportRecipient:
              selectedItem.transport &&
              selectedItem.transport.returnTransportRecipient
                ? selectedItem.transport.returnTransportRecipient
                : provingGround.returnTransportRecipient,
            returnTransportRecipientTime:
              selectedItem.transport &&
              selectedItem.transport.returnTransportRecipientTime,
            returnTransportRecipientDate:
              (selectedItem.transport &&
                selectedItem.transport.returnTransportRecipientDate) ||
              undefined,
          }
          break
        default:
          break
      }
      return {
        ...res,
      }
    }
    return undefined
  }

  useCommand(
    {
      key: 'transportInformation',
      disabled: selectedItems.length === 0 || !canUseCommand,
      text: t('model.proving.vehicle.transport'),
      iconProps: { iconName: 'Train' },
      priority: 50,
      subMenuProps: {
        items: [
          {
            key: 'outboundAndReturnTransport',
            text: t('enum.transport.outboundAndReturnTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.OutboundAndReturnTransport,
                ...getDefaultTransportData(
                  TransportType.OutboundAndReturnTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'outboundTransport',
            text: t('enum.transport.outboundTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.OutboundTransport,
                ...getDefaultTransportData(
                  TransportType.OutboundTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'returnTransport',
            text: t('enum.transport.returnTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.ReturnTransport,
                ...getDefaultTransportData(
                  TransportType.ReturnTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'noTransport',
            text: t('enum.transport.noTransport'),
            onClick: () => {
              handleNoTransport()
            },
          },
          {
            key: 'changeStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'vehicleTransportBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleTransportBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleTransportBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleTransportBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleTransportBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  useCommand(
    {
      key: 'annotation',
      text: t('field.material.annotation'),
      disabled: selectedItems.length === 0 || !canUseCommand,
      iconProps: { iconName: 'DietPlanNotebook' },
      headerText: t('model.proving.vehicle.transport'),
      onClick: () => {
        selectedItems.length > 1
          ? showMultiEditPanel({})
          : showMultiEditPanel({
              annotation: selectedItems[0].annotation,
            })
        setFormState(FormType.annotation)
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  useGroupCommand(
    t,
    proving,
    selectedItems,
    () => {
      showAddGroupPanel({ group: selectedItems[0].group })
    },
    handleRemoveGroup,
    selectedItems.length === 0 || !canUpdateCurrentProving
  )

  const handleDetailsViewSubmit = React.useCallback(
    async (_: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideEditPanel()
    },
    [hideEditPanel, refetch, selection]
  )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationMaterials: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  const onItemInvoked = (item: any): void => {
    setSelectedItemId(item._id)
    showEditPanel({
      ...item,
      material: item.material._id,
    })
  }
  const SapConfirmationText = (() => {
    const transport = proving.materialsTransport
    if (transport) {
      const outboundSender = transport.outboundTransportSender
        ? transport.outboundTransportSender.place
        : ''
      const outboundRecipient = transport.outboundTransportRecipient
        ? transport.outboundTransportRecipient.place
        : ''
      const returnSender = transport.returnTransportSender
        ? transport.returnTransportSender.place
        : ''
      const returnRecipient = transport.returnTransportRecipient
        ? transport.returnTransportRecipient.place
        : ''
      switch (transport.transportType) {
        case TransportType.OutboundTransport:
          return (
            <div>
              {t(`enum.transportType.${transport.transportType}`)}
              <br />
              {outboundSender}
              {' - '}
              {outboundRecipient}
            </div>
          )
        case TransportType.ReturnTransport:
          return (
            <div>
              {t(`enum.transportType.${transport.transportType}`)}
              <br />
              {returnSender}
              {' - '}
              {returnRecipient}
            </div>
          )
        case TransportType.OutboundAndReturnTransport:
          return (
            <div>
              {t(`enum.transportType.${transport.transportType}`)}
              <br />
              {outboundSender}
              {' - '}
              {outboundRecipient}
              <br />
              {returnSender}
              {' - '}
              {returnRecipient}
            </div>
          )
        default:
          break
      }
    }
    return <></>
  })()

  const handleMaterialsSAPExport = React.useCallback(async () => {
    setSapExportLoading(true)
    const transport = selectedItems[0] && selectedItems[0].transport
    if (transport) {
      const {
        outboundTransportSender,
        outboundTransportRecipient,
        returnTransportSender,
        returnTransportRecipient,
      } = transport
      await exportMaterialsToSap({
        variables: {
          provingId: proving.id,
          materialIds: selectedItems.map((s) => s._id),
          outboundTransportSender: outboundTransportSender
            ? outboundTransportSender.supplierNumber
            : undefined,
          outboundTransportRecipient: outboundTransportRecipient
            ? outboundTransportRecipient.supplierNumber
            : undefined,
          returnTransportSender: returnTransportSender
            ? returnTransportSender.supplierNumber
            : undefined,
          returnTransportRecipient: returnTransportRecipient
            ? returnTransportRecipient.supplierNumber
            : undefined,
          outboundTransport: outboundTransport ? true : false,
          returnTransport: returnTransport ? true : false,
        },
      })
    }
    setOutboundTransport(false)
    setReturnTransport(false)
    setSapExportLoading(false)
    hideConfirmTpoDialog()
    showTpoSuccessDialog()
    refetch()
  }, [
    selectedItems,
    proving,
    refetch,
    setSapExportLoading,
    exportMaterialsToSap,
    hideConfirmTpoDialog,
    showTpoSuccessDialog,
    outboundTransport,
    returnTransport,
  ])

  const returnExportMaterialsToSAPLabel = () => {
    if (selectedItems.length > 0) {
      if (selectedItems.some((item) => item.returnExportedToSAP === true)) {
        return t('model.proving.materials.alreadyExportedToSAP')
      } else {
        return t('model.proving.materials.returnExportToSAP')
      }
    } else {
      return t('model.proving.materials.returnExportToSAP')
    }
  }

  const outboundExportMaterialsToSAPLabel = () => {
    if (selectedItems.length > 0) {
      if (selectedItems.some((item) => item.outboundExportedToSAP === true)) {
        return t('model.proving.materials.alreadyExportedToSAP')
      } else {
        return t('model.proving.materials.outboundExportToSAP')
      }
    } else {
      return t('model.proving.materials.outboundExportToSAP')
    }
  }

  const handleTransportDataSubmit = React.useCallback(
    async (values: ProvingMaterialUpdateInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((material) => material._id)
      const batch: any[] = selectedItems.map((sel) =>
        sel.transport &&
        sel.transport.bookingStatus !== BookingStatus.NotRequired
          ? {
              _id: sel._id,
              update: {
                transport: {
                  ...values,
                },
              },
            }
          : {
              _id: sel._id,
              update: {
                transport: {
                  bookingStatus: BookingStatus.Open,
                  ...values,
                },
              },
            }
      )

      const { errors } = await updateProvingMaterials({
        variables: {
          batch,
          provingId: proving.id,
        },
      }).then((_) => refetch())

      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hideTransportDataPanel()
    },
    [hideTransportDataPanel, updateProving, proving, refetch, selectedItems]
  )

  const canCreateTPO = () => {
    return (
      isAdmin || isProvingResourceManager || isProvingEditor || isServiceTeam
    )
  }

  /* tslint:disable */
  /* eslint-disable */
  const isSameTransport = React.useCallback(
    (
      selectedMaterials: ProvingMaterial[],
      senderRecipientPrefix: 'outbound' | 'return'
    ) => {
      const firstItem = selectedMaterials[0]
      if (!firstItem.transport) {
        return false
      }

      const transport = firstItem.transport
      const firstSenderId =
        transport[`${senderRecipientPrefix}TransportSender`] &&
        transport[`${senderRecipientPrefix}TransportSender`]!._id
      const firstSenderDate =
        transport[`${senderRecipientPrefix}TransportSenderDate`]
      const firstRecipientId =
        transport[`${senderRecipientPrefix}TransportRecipient`] &&
        transport[`${senderRecipientPrefix}TransportRecipient`]!._id
      const firstRecipientDate =
        transport[`${senderRecipientPrefix}TransportRecipientDate`]

      return selectedMaterials.every((material) =>
        Boolean(
          !material.outboundExportedToSAP &&
            material.transport &&
            material.transport[`${senderRecipientPrefix}TransportSender`] &&
            material.transport[`${senderRecipientPrefix}TransportSender`]!
              ._id === firstSenderId &&
            material.transport[
              `${senderRecipientPrefix}TransportSenderDate`
            ] === firstSenderDate &&
            material.transport[`${senderRecipientPrefix}TransportRecipient`] &&
            material.transport[`${senderRecipientPrefix}TransportRecipient`]!
              ._id === firstRecipientId &&
            material.transport[
              `${senderRecipientPrefix}TransportRecipientDate`
            ] === firstRecipientDate
        )
      )
    },
    []
  )

  const canOutboundExportMaterialsToSAP = React.useMemo(() => {
    if (
      !(isAdmin || isProvingResourceManager || isProvingEditor || isServiceTeam)
    ) {
      return false
    }

    if (selectedItems.length < 1) {
      return false
    }

    return isSameTransport(selectedItems, 'outbound')
  }, [
    isAdmin,
    isProvingResourceManager,
    isProvingEditor,
    isServiceTeam,
    selectedItems,
    isSameTransport,
  ])

  const canReturnExportMaterialsToSAP = React.useMemo(() => {
    if (
      !(isAdmin || isProvingResourceManager || isProvingEditor || isServiceTeam)
    ) {
      return false
    }

    if (selectedItems.length < 1) {
      return false
    }

    return isSameTransport(selectedItems, 'return')
  }, [
    isAdmin,
    isProvingResourceManager,
    isProvingEditor,
    isServiceTeam,
    selectedItems,
    isSameTransport,
  ])

  useCommand(
    {
      key: 'exportOutboundMaterialsToSap',
      priority: 20,
      farCommand: true,
      text: outboundExportMaterialsToSAPLabel(),
      iconProps: { iconName: 'Export' },
      // Permissions in Development
      hidden: !canCreateTPO() || proving.type === ProvingType.Block,
      disabled: !canOutboundExportMaterialsToSAP,
      onClick: () => {
        setOutboundTransport(true)
        showConfirmTpoDialog()
      },
    },
    [selectedItems, canOutboundExportMaterialsToSAP, showConfirmTpoDialog]
  )

  useCommand(
    {
      key: 'exportReturnMaterialsToSap',
      priority: 21,
      farCommand: true,
      text: returnExportMaterialsToSAPLabel(),
      iconProps: { iconName: 'Export' },
      // Permissions in Development
      hidden: !canCreateTPO() || proving.type === ProvingType.Block,
      disabled: !canReturnExportMaterialsToSAP,
      onClick: () => {
        setReturnTransport(true)
        showConfirmTpoDialog()
      },
    },
    [selectedItems, canReturnExportMaterialsToSAP, showConfirmTpoDialog]
  )

  return (
    <Page>
      <Section label={t('section.materials.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUseCommand}
            onClick={() => {
              setFormState(FormType.annotationMaterials)
              showAnnotationPanel({
                annotation: proving.annotationMaterials || '',
              })
            }}
          />

          <RichTextViewer
            richText={
              proving.annotationMaterials ? proving.annotationMaterials : ''
            }
          />
        </AutoStack>
      </Section>
      {proving.provingGround && proving.provingGround.noteHeaderMaterials && (
        <Section
          label={
            (proving.provingGround &&
              proving.provingGround.noteHeaderMaterials) ||
            ''
          }
        >
          <Text2>
            {proving.provingGround && proving.provingGround.noteMaterials}
          </Text2>
        </Section>
      )}
      <Section label={t('section.materials.header')}>
        <AppCommandBar />
        <MaterialList
          items={materials}
          selection={selection}
          onItemInvoked={onItemInvoked}
        />
      </Section>
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}

      {formState !== undefined &&
        (formState === FormType.addMaterial ? (
          <FormikPanel
            {...panelState}
            onSubmit={handleAddMaterialSubmit}
            type={PanelType.extraLarge}
            validationSchema={new ProvingDetailsMaterialsFormSchema()}
          >
            <ProvingDetailsAddMaterialForm
              orderIds={proving.orderIdDistribution!.map((x: any) => x)}
              proving={proving}
            />
          </FormikPanel>
        ) : (
          <FormikPanel {...multiEditPanelState} onSubmit={handleSubmitMulti}>
            {formState === FormType.amount && <ProvingDetailsAmountForm />}
            {formState === FormType.orderId && (
              <ProvingDetailsOrderIdForm
                orderIds={proving.orderIdDistribution!.map((x: any) => x)}
              />
            )}
            {formState === FormType.transport && (
              <ProvingDetailsSimpleTransportForm />
            )}
            {formState === FormType.annotation && (
              <ProvingDetailsMaterialsAnnotationForm />
            )}
          </FormikPanel>
        ))}
      {formState !== undefined && formState === FormType.transportStatus && (
        <FormikPanel
          {...transportStatusPanelState}
          onSubmit={handleSubmitMulti}
        >
          <ProvingDetailsSimpleTransportStatusForm />
        </FormikPanel>
      )}
      <FormikPanel
        {...addGroupPanelState}
        onSubmit={handleGroupAssign}
        headerText={'Gruppe zuordnen'}
      >
        <ProvingDetailsGroupForm />
      </FormikPanel>
      <FormikPanel
        {...editPanelState}
        onSubmit={handleDetailsViewSubmit}
        type={PanelType.large}
      >
        <MaterialDetailsForm />
      </FormikPanel>
      <FormikPanel {...annotationPanelState} onSubmit={handleAnnotationSubmit}>
        {formState !== undefined &&
          formState === FormType.annotationMaterials && (
            <ProvingDetailsAnnotationForm />
          )}
      </FormikPanel>
      {formState !== undefined && formState === FormType.timespan && (
        <FormikPanel {...timespanPanelState} onSubmit={handleSubmitMulti}>
          <ProvingDetailsTimespanForm proving={proving!} />
        </FormikPanel>
      )}
      <FormikPanel
        {...transportDataPanelState}
        type={PanelType.large}
        onSubmit={handleTransportDataSubmit}
        headerText={'Transportinformationen'}
        validationSchema={ProvingTransportDetailsFormSchema}
      >
        <ProvingTransportDetailsForm
          showTransportTypeDropdown={false}
          dateFrom={selectedItems[0] && selectedItems[0].dateFrom}
          dateTo={selectedItems[0] && selectedItems[0].dateTo}
          disableDeliveryAndPickupDate={true}
          annotation={
            'Materialien dürfen erst zugestellt werden, wenn der Erprobungsleiter vor Ort ist.'
          }
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...deleteDialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      <YesNoDialog
        {...confirmTpoDialogState}
        onConfirm={handleMaterialsSAPExport}
        onDismiss={hideConfirmTpoDialog}
        dialogContentProps={{
          title: 'Möchten Sie folgenden Transportauftrag erstellen?',
        }}
        yesButtonText={t('action.createTpo')}
        noButtonText={t('action.cancel')}
        isLoading={sapExportLoading}
      >
        {SapConfirmationText}
      </YesNoDialog>
      <NotificationDialog
        {...tpoSuccessDialogState}
        onDismiss={hideTpoSuccessDialog}
        titleText={'Transportinformationen wurden versendet'}
      />
    </Page>
  )
}
